import type { MissingCapability } from "./capabilities.js";

/**
 * The screen a browser that cannot run cuekards gets instead of cuekards (web ticket
 * 17, stories 2 and 3).
 *
 * It is the whole page and not a banner over a working one, which is the
 * "plainly, rather than half-working" half of the ask: there is no camera
 * permission to grant here, no script box to fill in and no download to start.
 * Nothing has *run* behind it either — `main.tsx` renders this instead of calling
 * `boot`, so no worker is started, no permission is asked for and no model is
 * offered. The bundle is one file and is downloaded and parsed either way; what
 * the gate saves is everything that would have happened next.
 *
 * The copy is {@link MissingCapability}'s, decided in `capabilities.ts` and tested
 * there. This file is layout.
 *
 * **It ends at the list, and that is a decision rather than an omission.** The
 * desktop offer used to sit under it — the one thing there was to give somebody
 * whose machine cannot run this — and seo ticket 02 withdrew it everywhere. What
 * is left is better than a signup field for a product with no ship date: every
 * entry in the list carries its own `do`, and on a phone the single entry's is
 * "Open cuekards on a desktop or laptop in Chrome, Edge or another Chromium-based
 * browser", which is a real next step.
 */
export function CapabilityGate({
  missing,
}: {
  missing: readonly MissingCapability[];
}): JSX.Element {
  /** A phone is answered with exactly one entry, and it is not a list of faults —
   * see `missingCapabilities`. Saying "cuekards needs one more thing" over it would
   * be pretending the device is nearly there. */
  const oneThing = missing.length === 1;

  return (
    <main className="web-gate">
      <h1>cuekards</h1>
      <p className="web-tagline">A teleprompter that follows your voice.</p>

      <p className="web-gate-says" role="alert">
        {oneThing
          ? "cuekards can't run in this browser."
          : `cuekards can't run in this browser — ${missing.length} things it needs are missing.`}
      </p>

      <ul className="web-gate-list">
        {missing.map((entry) => (
          <li key={entry.what}>
            <strong>{entry.what}</strong>
            <span className="web-gate-why">{entry.why}</span>
            <span className="web-gate-do">{entry.do}</span>
          </li>
        ))}
      </ul>
    </main>
  );
}
